import { Client, getSessionToken, settings } from "../library/allauth";
import { getCSRFToken } from "../library/django";
import { AGGREGATION_LEVELS, DEFAULT_PAGE_SIZE } from "../constants/kpiConstants";

/**
 * Sanitize and validate input to prevent XSS
 */
export const sanitizeInput = (value) => {
  if (typeof value !== "string") return value;
  return value.replace(/[<>]/g, "").trim();
};

/**
 * Validate filter inputs
 */
export const validateFilters = (filters) => {
  const errors = [];

  const systemId = parseInt(filters.systemId);
  if (isNaN(systemId) || systemId < 1) {
    errors.push("System ID must be a positive number");
  }

  if (!filters.startDate || !filters.startTime) {
    errors.push("Start date and time are required");
  }

  if (filters.aggregationLevel && !AGGREGATION_LEVELS.includes(filters.aggregationLevel)) {
    errors.push("Invalid aggregation level");
  }

  return errors;
};

/**
 * Format date for backend API (preserves local timezone)
 */
export const formatDateForBackend = (dateString, timeString) => {
  if (!dateString || !timeString) return "";

  try {
    const [year, month, day] = dateString.split("-");
    const [hours, minutes, seconds = "00"] = timeString.split(":");

    if (!year || !month || !day || !hours || !minutes) return "";

    return `${year}-${month.padStart(2, "0")}-${day.padStart(
      2,
      "0"
    )} ${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}:${seconds.padStart(
      2,
      "0"
    )}`;
  } catch {
    return "";
  }
};

/**
 * Build query parameters for API calls
 */
export const buildQueryParams = (filters, options = {}) => {
  const { page = 1, pageSize = DEFAULT_PAGE_SIZE } = options;
  const query = new URLSearchParams();

  if (filters.systemId) {
    query.append("systemId", sanitizeInput(filters.systemId));
  }

  if (filters.interval) {
    query.append("interval", sanitizeInput(filters.interval));
  }

  if (filters.startDate && filters.startTime) {
    query.append(
      "startDate",
      formatDateForBackend(filters.startDate, filters.startTime)
    );
  }

  if (filters.endDate && filters.endTime) {
    query.append(
      "endDate",
      formatDateForBackend(filters.endDate, filters.endTime)
    );
  }

  if (filters.aggregationLevel) {
    query.append("aggregation_level", filters.aggregationLevel);
  }

  query.append("page", page.toString());
  query.append("page_size", pageSize.toString());

  return query;
};

/**
 * Generate table columns from data keys
 */
export const generateTableColumns = (data) => {
  if (!data || !Array.isArray(data) || data.length === 0) return [];

  const firstItem = data[0];
  return Object.keys(firstItem).map((key) => ({
    key,
    label: key
      .charAt(0)
      .toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1"),
    render: (value) => (
      <p className="text-xs sm:text-sm font-normal text-left text-[#131811]">
        {typeof value === "number" ? value.toLocaleString() : value ?? "N/A"}
      </p>
    ),
  }));
};

/**
 * Get API options with authentication
 */
export const getApiOptions = () => {
  const options = {
    headers: {},
    credentials: settings.withCredentials ? "include" : "same-origin",
  };

  const sessionToken = getSessionToken();
  if (settings.client === Client.BROWSER && sessionToken) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      options.headers["X-CSRFToken"] = csrfToken;
    }
  }

  return options;
};

/**
 * Calculate summary statistics from data
 */
export const calculateSummaryStats = (data) => {
  if (!data || data.length === 0) return null;

  const totalPedestrians = data.reduce(
    (sum, item) => sum + (item.Pedestrian || 0),
    0
  );
  const totalVehicles = data.reduce((sum, item) => sum + (item.Vehicle || 0), 0);
  const totalCyclist = data.reduce((sum, item) => sum + (item.Cyclist || 0), 0);
  const totalCount = data.reduce((sum, item) => sum + (item.total || 0), 0);
  const avgPedestrians = (totalPedestrians / data.length).toFixed(2);
  const avgVehicles = (totalVehicles / data.length).toFixed(2);

  return {
    totalPedestrians,
    totalVehicles,
    totalCyclist,
    totalCount,
    avgPedestrians,
    avgVehicles,
    records: data.length,
  };
};

/**
 * Handle API response and extract data
 */
export const handleApiResponse = (responseData, pageSize) => {
  // Handle paginated response
  if (responseData?.results && Array.isArray(responseData.results)) {
    const totalPages = Math.ceil(responseData.count / pageSize);
    return {
      data: responseData.results,
      pagination: {
        count: responseData.count || 0,
        totalPages,
        hasNext: !!responseData.next,
        hasPrevious: !!responseData.previous,
      },
    };
  }

  // Handle non-paginated response with "data" field
  if (responseData?.data && Array.isArray(responseData.data)) {
    return {
      data: responseData.data,
      pagination: {
        count: responseData.data.length,
        totalPages: 1,
        hasNext: false,
        hasPrevious: false,
      },
    };
  }

  // No valid data
  return {
    data: [],
    pagination: {
      count: 0,
      totalPages: 0,
      hasNext: false,
      hasPrevious: false,
    },
  };
};

/**
 * Load saved filters from session storage
 */
export const loadSavedFilters = (storageKey, defaultFilters) => {
  const savedFilter = sessionStorage.getItem(storageKey);
  if (!savedFilter) return null;

  try {
    const parsedFilter = JSON.parse(savedFilter);
    sessionStorage.removeItem(storageKey);
    
    return {
      systemId: parsedFilter.systemId || defaultFilters.systemId,
      streetId: parsedFilter.streetId ?? defaultFilters.streetId ?? "",
      interval: parsedFilter.interval || defaultFilters.interval,
      startDate: parsedFilter.startDate || defaultFilters.startDate,
      startTime: parsedFilter.startTime || defaultFilters.startTime,
      endDate: parsedFilter.endDate || defaultFilters.endDate || "",
      endTime: parsedFilter.endTime || defaultFilters.endTime || "",
      aggregationLevel: parsedFilter.aggregationLevel || defaultFilters.aggregationLevel,
    };
  } catch (err) {
    console.error("Error parsing saved filter:", err);
    return null;
  }
};